<template>
  <v-container fluid>
    <EditDetailDialog v-model="editDetailViewModel" :save-callback="onEditDetail" />

    <AddDetailDialog v-model="addDetailViewModel" :save-callback="onAddDetail" />

    <ClaimEntry v-model="claimEntryViewModel" :save-callback="onClaimEntrySave" />

    <v-card flat width="100%" :loading="loading" :disabled="loading || submitting">
      <v-card-title class="pa-0">
        <span>{{ !!id ? 'Изменение' : 'Добавление' }} заявки</span>
        <v-spacer />
        <span>{{ requestTypeName }}</span>
      </v-card-title>

      <v-divider class="mb-2" />

      <!-- Данные заявки -->
      <v-row dense>
        <v-col>
          <CargoTypeSelector v-model="request.cargoTypeID" dense :can-any="false" :disabled="!!id" />
        </v-col>

        <!-- Номер заявки -->
        <v-col class="pb-0">
          <v-text-field v-model="request.number" dense label="Номер заявки" aria-autocomplete="off"
            :readonly="!canEditRequestNumberAndDate" :hide-details="!canEditRequestNumberAndDate"
            :error-messages="objectErrors($v.request.number)" :counter="$v.request.number.$params.maxLength.max"
            @input="$v.request.number.$touch()" @blur="$v.request.number.$touch()" />
        </v-col>

        <!-- Дата заяки - планируемая дата обработки заявки -->
        <v-col class="pb-0">
          <DateInput v-model="request.date" dense label="Дата заявки" tooltip="Планируемая дата прихода или расхода"
            :disabled="!canEditRequestNumberAndDate" :error-messages="objectErrors($v.request.date)"
            @input="$v.request.date.$touch()" @blur="$v.request.date.$touch()" />
        </v-col>

        <v-col v-if="!!id" class="pb-0">
          <v-tooltip>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-bind="attrs" dense readonly hide-details label="Создана" aria-autocomplete="off"
                :value="$dateTimeFormat(request.createTimeStamp)" v-on="on" />
            </template>
            <span>Дата и время создания заявки</span>
          </v-tooltip>
        </v-col>

        <v-col v-if="!!id" class="pb-0">
          <v-tooltip>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-bind="attrs" dense readonly hide-details label="Обработана" aria-autocomplete="off"
                :value="request.proceedDate ? $dateFormat(request.proceedDate) : '[ нет данных ]'" v-on="on" />
            </template>
            <span>Дата завершения разгрузки или погрузки машины</span>
          </v-tooltip>
        </v-col>

        <v-col v-if="!!id" class="pb-0">
          <v-tooltip>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field v-bind="attrs" dense readonly hide-details label="Документы" aria-autocomplete="off"
                :value="request.reportPrintTime ? $dateTimeFormat(request.reportPrintTime) : '[ нет данных ]'"
                v-on="on" />
            </template>
            <span>Дата и время печати документов</span>
          </v-tooltip>
        </v-col>
      </v-row>

      <!-- Данные накладной -->
      <v-row dense>
        <!-- Номер накладной -->
        <v-col v-if="request.requestTypeID === 1 && !!id" cols="2">
          <v-text-field v-model="request.invoiceNum" dense label="Номер накладной" aria-autocomplete="off" counter="30"
            :readonly="!canEditInvoiceNumberAndDate" :hide-details="!canEditInvoiceNumberAndDate" />
        </v-col>

        <!-- Дата приходной накладной или дата отгрузки заявки для расхода -->
        <v-col v-if="request.requestTypeID === 1 && !!id" cols="2">
          <DateInput v-model="request.invoiceDate" dense label="Дата накладной"
            :disabled="!canEditInvoiceNumberAndDate" />
        </v-col>

        <!-- Контрагент -->
        <v-col>
          <v-text-field v-model="request.counterpart" dense :readonly="!canEditCounterpart" label="Контрагент"
            :error-messages="objectErrors($v.request.counterpart)"
            :counter="$v.request.counterpart.$params.maxLength.max" @input="$v.request.counterpart.$touch()"
            @blur="$v.request.counterpart.$touch()" />
        </v-col>
      </v-row>

      <!-- Данные по машине -->
      <v-row dense>
        <!-- Номер машины -->
        <v-col class="pb-0">
          <v-text-field v-model="request.car" dense label="Номер машины" aria-autocomplete="off"
            :hide-details="!canEditCarAndDriver" :readonly="!canEditCarAndDriver"
            :error-messages="objectErrors($v.request.car)" :counter="$v.request.car.$params.maxLength.max"
            @input="$v.request.car.$touch()" @blur="carBlur(); $v.request.car.$touch()" />
        </v-col>

        <!-- Водитель -->
        <v-col class="pb-0">
          <v-text-field v-model="request.driver" dense label="Водитель" aria-autocomplete="off"
            :readonly="!canEditCarAndDriver" :hide-details="!canEditCarAndDriver"
            :error-messages="objectErrors($v.request.driver)" :counter="$v.request.driver.$params.maxLength.max"
            @input="$v.request.driver.$touch()" @blur="driverBlur(); $v.request.driver.$touch()" />
        </v-col>

        <!-- Время въезда машины на склад -->
        <v-col v-if="!!id" class="pb-0">
          <v-text-field dense readonly hide-details label="Въезд машины" aria-autocomplete="off"
            :value="request.carQueue && request.carQueue.carTimeIn ? $dateTimeFormat(request.carQueue.carTimeIn) : '[ нет данных ]'" />
        </v-col>

        <!-- Время выезда машины со склада -->
        <v-col v-if="!!id" class="pb-0">
          <v-text-field dense readonly hide-details label="Выезд машины" aria-autocomplete="off"
            :value="request.carQueue && request.carQueue.carTimeOut ? $dateTimeFormat(request.carQueue.carTimeOut) : '[ нет данных ]'" />
        </v-col>
      </v-row>

      <v-row dense class="my-4">
        <ActionButton icon="mdi-printer" color="blue" title="Печать" :disabled="!canPrint" :loading="printButtonBusy"
          @click="print()" />
        <v-spacer />
        <ActionButton icon="mdi-microsoft-excel" color="green" title="Экспорт" :disabled="!canExport"
          :loading="exportButtonBusy" @click="exportClick" />
      </v-row>

      <!-- Детали заявки -->
      <v-data-table dense :items="request.requestDetails" :headers="detailsHeaders" :loading="loading" item-key="id"
        disable-pagination disable-sort disable-filtering hide-default-footer loading-text="Загрузка данных..."
        no-data-text="Добавьте товары в заявку..." class="styled-table" :class="{ 'pointed-table': canEditDetails }"
        @click:row="detailClick">
        <template v-slot:[`item.productionDate`]="{ item }">
          {{ item.productionDate | dateFormat }}
        </template>

        <template v-slot:[`item.qiPackage`]="{ item }">
          {{ Math.ceil(item.qty / item.good.qiPackage) }}
        </template>

        <template v-slot:[`item.qiPallet`]="{ item }">
          {{ Math.ceil(item.qty / item.good.qiPallet) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <template v-if="canEditDetails">
            <!-- Открыть/редактировать претензию -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon :color="getClaimIconColor(item)" class="mr-1" v-bind="attrs" v-on="on"
                  @click.stop="openClaimEntry(item)">
                  mdi-file-cancel-outline
                </v-icon>
              </template>
              <span>{{ getClaimActionText(item) }}</span>
            </v-tooltip>

            <!-- Удалить претензию (только если статус = Added) -->
            <v-tooltip v-if="item.requestClaim && !item.requestClaim.isDeleted && item.requestClaim.status === 0"
              bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="red" class="mr-1" v-bind="attrs" v-on="on" @click.stop="deleteClaim(item)">
                  mdi-close-circle-outline
                </v-icon>
              </template>
              <span>Удалить претензию</span>
            </v-tooltip>

            <!-- Удалить деталь -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="red" v-bind="attrs" v-on="on" @click.stop="deleteDetail(item)">
                  mdi-delete
                </v-icon>
              </template>
              <span>Удалить позицию</span>
            </v-tooltip>
          </template>
        </template>
      </v-data-table>

      <v-card-actions class="px-0">
        <v-spacer />
        <v-btn v-if="changed" class="px-6" outlined @click.prevent="cancelClick">
          Отмена
        </v-btn>
        <v-btn v-if="changed" class="px-4" color="primary" :loading="submitting" @click.prevent="saveClick">
          Сохранить
        </v-btn>
        <v-btn v-else class="px-4" color="primary" @click.prevent="cancelClick">
          Закрыть
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- место для кнопки добавить -->
    <v-sheet height="48" />
    <!-- кнопка добавить -->
    <v-btn v-if="canEditDetails" color="primary" dark fab fixed bottom right @click.prevent="addButtonClick()">
      <v-icon class="white--text">
        mdi-plus
      </v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import DateInput from "@/components/DateInput"
import CargoTypeSelector from "@/components/CargoTypeSelector"
import ActionButton from "@/components/ActionButton"
import EditDetailDialog from "./EditDetailDialog.vue"
import AddDetailDialog from "./AddDetailDialog.vue"
import ClaimEntry from "../../components/ClaimEntry.vue"
import { required, maxLength, minLength } from "vuelidate/lib/validators"
import tools from "@/utils/tools"

const lastDateKey = "RequestAdd.LastDate"
export default {
  components: {
    DateInput,
    CargoTypeSelector,
    ActionButton,
    AddDetailDialog,
    EditDetailDialog,
    ClaimEntry
  },

  props: {
    id: {
      type: String,
      default: ""
    }
  },

  data: () => ({
    // диалог редактирования количества
    editDetailViewModel: null,
    // диалог выбора товара
    addDetailViewModel: null,
    // диалог ввода претензии
    claimEntryViewModel: null,
    usePartNumber: true,
    // загрузка данных заявки
    loading: false,
    // сохранение заявки
    submitting: false,
    printButtonBusy: false,
    exportButtonBusy: false,
    // объект заявки
    request: {
      number: Number,
      car: "",
      driver: "",
      requestDetails: []
    },
    oldCar: "",
    oldDriver: "",
    oldInvoiceDate: "",
    oldInvoiceNum: "",
    oldRequestDate: "",
    oldRequestNum: "",
    oldCounterpart: "",
    deletedDetails: []
  }),

  validations() {
    return {
      request: {
        number: { required, maxLength: maxLength(30) },
        date: { required },
        counterpart: { maxLength: maxLength(254) },
        car: { required, minLength: minLength(5), maxLength: maxLength(80) },
        driver: { required, minLength: minLength(5), maxLength: maxLength(60) }
      }
    }
  },

  computed: {
    isRequired() {
      return true
    },
    detailsHeaders() {
      const headers = [
        { text: "Артикул", value: "good.article", class: "tableHeader tableHeaderText--text" },
        { text: "Наименование", value: "good.name", class: "tableHeader tableHeaderText--text" },
        { text: "Штук", value: "qty", class: "tableHeader tableHeaderText--text" },
        { text: "Упаковок", value: "qiPackage", class: "tableHeader tableHeaderText--text" },
        { text: "Поддонов", value: "qiPallet", class: "tableHeader tableHeaderText--text" }
      ]

      if (this.usePartNumber) {
        // Номер партии всегда
        headers.push({ text: "Партия", value: "partNumber", class: "tableHeader tableHeaderText--text" })
      } else if (this.request.requestTypeID == 2) {
        // Дата производства при расходе
        headers.push({
          text: "Дата произв.",
          value: "productionDate",
          class: "tableHeader tableHeaderText--text"
        })
      }

      if (this.canEditDetails)
        headers.push({
          text: "",
          value: "actions",
          width: "96px", // ширина для двух иконок
          class: "tableHeader tableHeaderText--text"
        })
      return headers
    },

    canEditInvoiceNumberAndDate() {
      return (
        !!this.id &&
        this.request.requestTypeID === 1 &&
        (this.$store.state.auth.user.role === "Управляющий" ||
          this.$store.state.auth.user.role === "Администратор" ||
          this.$store.state.auth.user.role === "Кладовщик")
      )
    },

    canEditRequestNumberAndDate() {
      // может менять клиент пока статус = 10 или заявка только создаётся
      return (
        !this.id ||
        (this.request.requestStatusID <= 10 && // получена
          (this.$store.state.auth.user.role === "Клиент" || this.$store.state.auth.user.role === "Администратор"))
      )
    },

    canEditCounterpart() {
      return (
        !this.id ||
        (this.request.requestStatusID <= 10 && // получена
          (this.$store.state.auth.user.role === "Клиент" || this.$store.state.auth.user.role === "Администратор"))
      )
    },

    canEditCarAndDriver() {
      return (
        !this.id ||
        (this.request.requestStatusID <= 10 && // получена
          this.$store.state.auth.user.role !== "Регистратор")
      )
    },

    canEditDetails() {
      return (
        true ||
        (!this.loading &&
          !this.submitting &&
          (!this.id ||
            (this.request.requestStatusID <= 10 && // получена
              (this.$store.state.auth.user.role === "Клиент" || this.$store.state.auth.user.role === "Администратор"))))
      )
    },

    requestTypeName() {
      // если загружена шапка и типы
      return this.loading || this.$store.getters["directories/loading"]
        ? ""
        : this.$store.getters["directories/getRequestType"](this.request.requestTypeID).name
    },

    canExport() {
      return this.$store.state.auth.user.role !== "Регистратор" && this.$store.state.auth.user.role !== "Кладовщик"
    },

    canPrint() {
      return (
        this.$store.state.auth.user.role !== "Регистратор" &&
        this.$store.state.auth.user.role !== "Клиент" &&
        !this.loading &&
        !this.changed &&
        this.request.id &&
        (this.request.trackersTakedOff || !this.request.cargoType.useTrackers) &&
        (this.request.requestTypeID === 1
          ? this.request.requestStatusID >= 50 // Выгружена или обработана
          : this.request.requestStatusID === 110) // Загружена
      )
    },

    // true, если есть изменения в заявке
    changed() {
      return (
        this.oldDriver !== this.request.driver ||
        this.oldCar !== this.request.car ||
        this.oldCounterpart != this.request.counterpart ||
        this.oldRequestDate != (this.request.date ?? "").substring(0, 10) ||
        this.oldRequestNum != this.request.number ||
        this.oldInvoiceDate != (this.request.invoiceDate ?? "").substring(0, 10) ||
        this.oldInvoiceNum != this.request.invoiceNum ||
        this.deletedDetails.length ||
        this.request.requestDetails.findIndex((e) => e.id < 0) !== -1 // есть добавленные записи
      )
    }
  },

  async mounted() {
    if (!this.id) {
      // /////////// Создание заявки ///////////
      this.usePartNumber = this.$store.getters["cargoTypes/getItemByID"](this.request.cargoTypeID).usePartNumber
      // генерация номера заявки
      this.request.number = await this.getNewNumber(this.request.cargoTypeID, this.request.requestTypeID)
    }
  },

  async created() {
    document.addEventListener("keydown", this.keydown)

    if (this.id) {
      // /////////// Редактирование заявки ///////////

      // данные заявки
      try {
        this.loading = true
        const response = await this.axios(`/requests/get/${this.id}`)
        this.request = response.data
        this.request.requestDetails = this.sortDetails(this.request.requestDetails)
        this.oldCar = this.request.car
        this.oldDriver = this.request.driver
        this.oldCounterpart = this.request.counterpart
        this.oldRequestDate = (this.request.date ?? "").substring(0, 10)
        this.oldRequestNum = this.request.number
        this.oldInvoiceDate = (this.request.invoiceDate ?? "").substring(0, 10)
        this.oldInvoiceNum = this.request.invoiceNum
        this.usePartNumber = this.$store.getters["cargoTypes/getItemByID"](this.request.cargoTypeID).usePartNumber
      } catch (err) {
        this.$router.push("/error?message=Ошибка при загрузке данных заявки")
      } finally {
        this.loading = false
      }
    } else {
      // /////////// Создание заявки ///////////

      // определение типа заявки по роуту
      this.request.requestTypeID = this.$route.query.requestType ? parseInt(this.$route.query.requestType) : 1
      // генерация даты заявки
      // по умолчанию - завтрашний день
      let date = new Date(new Date().getTime() + 1000 * 60 * 60 * 24)
      const today = new Date(new Date().toISOString().substring(0, 10))
      const storageObj = localStorage.getItem(lastDateKey)
      if (storageObj) {
        const { timeStamp, lastDate } = JSON.parse(storageObj)
        if (lastDate && timeStamp && new Date(lastDate) >= today && new Date(timeStamp) > today)
          date = new Date(lastDate)
      }
      this.request.date = date.toISOString().substring(0, 10)
    }
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.keydown)
  },

  methods: {
    keydown(value) {
      if (value.code === "NumpadAdd" && this.canEditDetails) {
        this.addButtonClick()
      }
    },

    cancelClick() {
      this.$router.push("/requests")
    },

    async exportClick() {
      try {
        if (this.changed && !(await this.save())) {
          return
        }
        this.exportButtonBusy = true
        const response = await this.axios.get(`/requests/export/${this.request.id}`)
        window.open(response.data, "_blank")
      } catch (err) {
        this.$notify("error", "Ошибка при экспорте заявки")
      } finally {
        this.exportButtonBusy = false
      }
    },

    async print() {
      try {
        if (this.changed && !(await this.save())) {
          return
        }
        this.printButtonBusy = true
        const response = await this.axios.get(`/requests/print/${this.request.id}`)
        window.open(response.data, "_blank")
      } catch (err) {
        this.$notify("error", err.response?.data?.message ? err.response.data.message : "Ошибка при печати документов")
      } finally {
        this.printButtonBusy = false
      }
    },

    objectErrors(obj) {
      if (!obj.$dirty) return []
      const errors = []

      obj.$params.maxLength && !obj.maxLength && errors.push(`Максимум ${obj.$params.maxLength.max} символов`)

      obj.$params.minLength &&
        !obj.minLength &&
        errors.push(`Не менее ${obj.$params.minLength.min} символов, пожалуйста`)

      obj.$params.maxLength &&
        !obj.maxLength &&
        errors.push(`Не более ${obj.$params.maxLength.max} символов, пожалуйста`)

      obj.$params.required && !obj.required && errors.push("Введите значение")

      return errors
    },

    async getNewNumber(cargoTypeID, requestTypeID) {
      try {
        const response = await this.axios.get("/requests/getNewNumber", {
          params: {
            cargoTypeID: cargoTypeID,
            requestTypeID: requestTypeID
          }
        })
        return response.data
      } catch (err) {
        this.$notify(
          "error",
          err.response?.data?.message ? err.response.data.message : "Ошибка при генерации номера заявки"
        )
      }
    },

    // сохранение заявки
    async save() {
      if (this.$v.$invalid) {
        this.$v.$touch()
        return false
      }
      // защита от дуры
      if (this.exceedsPalletQty(this.request.requestDetails)) return false
      try {
        this.submitting = true
        // изменение номера и водителя
        if (this.id) {
          await this.axios.patch("/requests/updateRequest", {
            id: this.id,
            car: this.request.car,
            driver: this.request.driver,
            counterpart: this.request.counterpart,
            date: this.request.date,
            number: this.request.number,
            invoiceDate: this.request.invoiceDate,
            invoiceNum: this.request.invoiceNum,
            deletedDetailIDs: this.deletedDetails,
            details: this.request.requestDetails
          })
          this.oldCar = this.request.car
          this.oldDriver = this.request.driver
          this.oldCounterpart = this.request.counterpart
          this.oldRequestDate = (this.request.date ?? "").substring(0, 10)
          this.oldRequestNum = this.request.number
          this.oldInvoiceDate = (this.request.invoiceDate ?? "").substring(0, 10)
          this.oldInvoiceNum = this.request.invoiceNum
          this.$notify("success", "Данные сохранены")
        } else {
          await this.axios.post("/requests/insertRequest", {
            cargoTypeID: this.request.cargoTypeID,
            requestTypeID: this.request.requestTypeID,
            car: this.request.car,
            driver: this.request.driver,
            counterpart: this.request.counterpart,
            date: this.request.date,
            number: this.request.number,
            details: this.request.requestDetails
          })
          this.$notify("success", "Заявка создана")
          // сохранить дату
          localStorage.setItem(lastDateKey, JSON.stringify({ timeStamp: new Date(), lastDate: this.request.date }))
        }
        return true
      } catch (err) {
        this.$notify("error", err.response?.data?.message ? err.response.data.message : "Ошибка при сохранении заявки")
      } finally {
        this.submitting = false
      }
      return false
    },

    async saveClick() {
      if (await this.save()) this.$router.push("/requests")
    },

    // удаление детали
    deleteDetail(detail) {
      if (detail.id > 0) {
        this.deletedDetails.push(detail.id)
      }
      const index = this.request.requestDetails.findIndex((el) => el.id === detail.id)
      this.request.requestDetails.splice(index, 1)
    },

    async deleteClaim(detail) {
      // Проверка, что претензия существует
      if (!detail.requestClaim || !detail.requestClaim.id) {
        this.$notify("warning", "Претензия не найдена")
        return
      }

      // Проверка статуса претензии (0 = Added)
      if (detail.requestClaim.status !== 0) {
        const statusMap = {
          1: "Подписана",
          2: "Отклонена"
        }
        const statusName = statusMap[detail.requestClaim.status] || `статус ${detail.requestClaim.status}`
        this.$notify("warning", `Нельзя удалить претензию в статусе "${statusName}". Удалить можно только претензию в статусе "Добавлена"`)
        return
      }

      // Подтверждение удаления
      if (!confirm("Вы уверены, что хотите удалить эту претензию?")) {
        return
      }

      try {
        // Вызов API удаления
        await this.axios.post(`/requests/DeleteIncident/${detail.requestClaim.id}`)

        // Удаляем претензию из локального объекта
        delete detail.requestClaim

        this.$notify("success", "Претензия удалена")

        // Обновляем данные заявки для синхронизации с сервером
        await this.reloadRequest()
      } catch (err) {
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.title ||
          "Ошибка при удалении претензии"
        this.$notify("error", errorMessage)
      }
    },

    // В openClaimEntry добавить partNumber
    openClaimEntry(detail) {
      // претензия привязывается к сохранённой детали по её ID
      if (!detail.id || detail.id < 0) {
        this.$notify("warning", "Сначала сохраните заявку, затем оформляйте претензию по позиции")
        return
      }
      const existingClaim = detail.requestClaim;

      // Максимальное количество в упаковках (как в претензии)
      const maxQtyInPackages = Math.floor(detail.qty / detail.good.qiPackage);

      if (existingClaim && !existingClaim.isDeleted) {
        // Check if claim can be edited (only status 0 = Added)
        const canEdit = existingClaim.status === 0;

        this.claimEntryViewModel = {
          id: existingClaim.id,
          requestDetailId: detail.id,
          cargoTypeID: existingClaim.cargoTypeID,
          requestId: existingClaim.requestId,
          goodId: existingClaim.goodId,
          goodArticle: detail.good.article,
          goodName: detail.good.name,
          partNumber: detail.partNumber,
          qty: existingClaim.qty,
          maxQty: maxQtyInPackages, // Добавляем максимальное количество
          unitPrice: existingClaim.price,
          description: existingClaim.comment,
          isEdit: canEdit,
          isReadOnly: !canEdit,
          status: existingClaim.status
        }
      } else {
        // New claim - всегда редактируема
        this.claimEntryViewModel = {
          id: -1,
          requestDetailId: detail.id,
          cargoTypeID: this.request.cargoTypeID,
          requestId: this.request.id,
          goodId: detail.good.id,
          goodArticle: detail.good.article,
          goodName: detail.good.name,
          partNumber: detail.partNumber,
          qty: 0, // Начальное значение
          maxQty: maxQtyInPackages, // Добавляем максимальное количество
          unitPrice: null,
          description: "",
          isEdit: false,
          isReadOnly: false,
          status: 0
        }
      }
    },

    // претензия сохраняется по ID детали; заявка, товар и партия берутся на сервере из детали
    async onClaimEntrySave(claimData) {
      try {
        const response = await this.axios.post("/requests/AddOrUpdateIncident", {
          requestDetailId: claimData.requestDetailId,
          qty: claimData.qty,
          price: claimData.unitPrice,
          comment: claimData.description,
          userId: this.$store.state.auth.user.id
        }, {
          maxRedirects: 0,
          validateStatus: (status) => status >= 200 && status < 400
        })

        let claimData_response;

        // Проверяем, был ли редирект (302) или прямой ответ
        if (response.status === 302 || response.headers.location) {
          // PRG: переходим по URL из Location header
          const redirectUrl = response.headers.location;
          const getResponse = await this.axios.get(redirectUrl);
          claimData_response = getResponse.data;
        } else {
          // Если бэкенд вернул данные напрямую (CreatedAtAction)
          if (response.data && response.data.id) {
            const getResponse = await this.axios.get(`/requests/GetClaim/${response.data.id}`);
            claimData_response = getResponse.data;
          }
        }

        // Обновляем локальные данные
        if (claimData_response) {
          const detail = this.request.requestDetails.find(d => d.id === claimData.requestDetailId)

          if (detail) {
            detail.requestClaim = claimData_response;
          }
        }

        // Защита от повторного POST при F5
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

        this.$notify("success", "Претензия сохранена")
        await this.reloadRequest()
        return true
      } catch (err) {
        this.$notify("error", err.response?.data?.message || "Ошибка при сохранении претензии")
        return false
      }
    },

    async reloadRequest() {
      try {
        this.loading = true
        const response = await this.axios(`/requests/get/${this.id}`)
        this.request = response.data
        this.request.requestDetails = this.sortDetails(this.request.requestDetails)

        this.oldCar = this.request.car
        this.oldDriver = this.request.driver
        this.oldCounterpart = this.request.counterpart
        this.oldRequestDate = (this.request.date ?? "").substring(0, 10)
        this.oldRequestNum = this.request.number
        this.oldInvoiceDate = (this.request.invoiceDate ?? "").substring(0, 10)
        this.oldInvoiceNum = this.request.invoiceNum
        this.deletedDetails = []

      } catch (err) {
        this.$notify("error", "Ошибка при обновлении данных заявки")
      } finally {
        this.loading = false
      }
    },

    // диалог по кнопке добавить
    addButtonClick() {
      this.addDetailViewModel = {
        usePartNumber: this.usePartNumber,
        requestTypeID: this.request.requestTypeID,
        cargoTypeID: this.request.cargoTypeID,
        id: -1
      }
    },

    // добавление детали в заявку
    async onAddDetail(detail) {
      // защита от дуры - нельзя превышать количество поддонов по позиции
      if (this.exceedsGoodPalletQty(detail.qty, detail.qiPackage, detail.qiPallet)) return false

      // нельзя допускать создания позиций с одинаковым артикулом и датой/партии
      if (
        this.request.requestDetails.find(
          (d) =>
            d.good.id === detail.goodID &&
            d.partNumber === detail.partNumber &&
            (d.productionDate?.substring(0, 10) ?? null) === detail.productionDate
        )
      ) {
        const msg = this.usePartNumber ? "одинаковым номером партии" : "одинаковой датой производства"
        this.$notify("warning", `Нельзя допускать создание нескольких позиций с ${msg}`)
        return false
      }

      const newDetail = {
        id: Math.min(0, ...this.request.requestDetails.filter((d) => d.id < 0).map((x) => x.id)) - 1,
        good: {
          id: detail.goodID,
          article: detail.goodArticle,
          name: detail.goodName,
          qiPackage: detail.qiPackage,
          qiPallet: detail.qiPallet
        },
        qty: detail.qty * detail.qiPackage,
        partNumber: detail.partNumber,
        productionDate: detail.productionDate
      }
      this.request.requestDetails.push(newDetail)
      this.request.requestDetails = this.sortDetails(this.request.requestDetails)
      return true
    },

    // редактирование
    detailClick(value) {
      if (!this.canEditDetails) return
      this.editDetailViewModel = {
        usePartNumber: this.usePartNumber,
        requestTypeID: this.request.requestTypeID,
        id: value.id,
        goodName: value.good.name,
        goodArticle: value.good.article,
        qiPackage: value.good.qiPackage,
        qiPallet: value.good.qiPallet,
        qty: value.qty / value.good.qiPackage,
        partNumber: value.partNumber,
        productionDate: value.productionDate
      }
    },

    // сохранение детали (в памяти)
    async onEditDetail(detail) {
      const oldDetail = this.request.requestDetails.find((d) => d.id == detail.id)
      // защита от дуры - нельзя превышать количество поддонов по позиции
      if (this.exceedsGoodPalletQty(detail.qty, detail.qiPackage, detail.qiPallet)) return false
      // нельзя допускать редактирования позиций, кт. приведут к созданию позиций с одинаковым артикулом и датой/партии
      const exists = this.request.requestDetails.find(
        (d) =>
          d.id !== detail.id &&
          d.good.id === oldDetail.good.id &&
          d.partNumber === detail.partNumber &&
          (d.productionDate?.substring(0, 10) ?? null) === detail.productionDate
      )
      if (exists) {
        const msg = this.usePartNumber ? "одинаковым номером партии" : "одинаковой датой производства"
        this.$notify("warning", `Нельзя допускать создание нескольких позиций с ${msg}`)
        return false
      }

      oldDetail.qty = detail.qty * detail.qiPackage
      oldDetail.partNumber = detail.partNumber
      oldDetail.productionDate = detail.productionDate
      if (detail.id > 0) {
        oldDetail.id = Math.min(0, ...this.request.requestDetails.filter((d) => d.id < 0).map((x) => x.id)) - 1
        this.deletedDetails.push(detail.id)
      }
      return true
    },

    // проверка на превышение количества поддонов по позиции
    exceedsGoodPalletQty(qty, qiPackage, qiPallet) {
      // защита от дуры
      const maxPalletLimit = 33
      const currentPalletQty = Math.ceil((qty * qiPackage) / qiPallet)
      if (currentPalletQty > maxPalletLimit) {
        this.$notify(
          "warning",
          `Количество поддонов по позиции ${currentPalletQty} превышает максимальное количество ${maxPalletLimit}`
        )
        return true
      }
      return false
    },

    // проверка на превышение количества поддонов по всей заявке
    exceedsPalletQty(details) {
      // защита от дуры
      const maxPalletLimit = 33
      const currentPalletQty = details.reduce((acc, c) => acc + Math.ceil(c.qty / c.good.qiPallet), 0)
      if (currentPalletQty > maxPalletLimit) {
        this.$notify(
          "warning",
          `Количество поддонов по заявке ${currentPalletQty} превышает максимальное количество ${maxPalletLimit}`
        )
        return true
      }
      return false
    },

    sortDetails(src) {
      // временный массив содержит объекты с позицией и значением для сортировки
      var mapped = src.map(function (el, i) {
        return {
          index: i,
          value: {
            article: el.good.article,
            partNumber: el.partNumber,
            productionDate: new Date(el.productionDate)
          }
        }
      })

      // сортируем массив, содержащий уменьшенные значения
      mapped.sort(function (a, b) {
        if (a.value.article > b.value.article) {
          return 1
        }
        if (a.value.article < b.value.article) {
          return -1
        }

        if (a.value.partNumber > b.value.partNumber) {
          return 1
        }
        if (a.value.partNumber < b.value.partNumber) {
          return -1
        }

        if (a.value.productionDate > b.value.productionDate) {
          return 1
        }
        if (a.value.productionDate < b.value.productionDate) {
          return -1
        }

        return 0
      })

      return mapped.map(function (el) {
        return src[el.index]
      })
    },

    carBlur() {
      this.request.car = tools.carNumberNormalize(this.request.car)
    },

    driverBlur() {
      this.request.driver = tools.driverNormalize(this.request.driver)
    },

    canDeleteClaim() {
      return (detail) => {
        if (!detail.requestClaim) return false
        // Статус 0 = Added (Добавлена)
        return detail.requestClaim.status === 0
      }
    },

    getClaimStatusName(status) {
      const statusMap = {
        0: "Добавлена",
        1: "Подписана",
        2: "Отклонена"
      }
      return statusMap[status] || "Неизвестно"
    },

    getClaimStatusColor(status) {
      const colorMap = {
        0: 'grey',
        1: 'green',
        2: 'red'
      }
      return colorMap[status] || 'grey'
    },

    getClaimIconColor(item) {
      if (!item.requestClaim || item.requestClaim.isDeleted) {
        return 'grey' // Нет претензии
      }
      switch (item.requestClaim.status) {
        case 0: return 'orange' // Добавлена - можно редактировать
        case 1: return 'green'  // Подписана - только просмотр
        case 2: return 'red'    // Отклонена - только просмотр
        default: return 'grey'
      }
    },

    getClaimActionText(item) {
      if (!item.requestClaim || item.requestClaim.isDeleted) {
        return 'Добавить претензию'
      }
      switch (item.requestClaim.status) {
        case 0: return 'Редактировать претензию'
        case 1: return 'Просмотр претензии (Подписана)'
        case 2: return 'Просмотр претензии (Отклонена)'
        default: return 'Просмотр претензии'
      }
    }
  }
}
</script>
